import { useCallback, useEffect, useRef, useState } from 'react';
import { StartOverlay } from '@/components/deai-startoverlay';
import { DEAI3DScene } from '@/components/deai-3dscene';
import { DEAIApp } from '@/components/deai-app';
import { useAppStore } from '@/hooks/use-app-store';
import { PERSONAS } from '@/types/persona';
import { useVirtualIdolContextStore } from '@/hooks/virtualidol/use-virtual-idol-context-store';
import { useDEAIChat } from '@/hooks/use-deai-chat';
import { expressionPatterns, vrmExpressionController } from '@/lib/vrm-expression-controller';

const lucySystemPrompt = `You are Tori, an employee of a restaurant called “HootHoot” who is facing losing her job to private equity

CHARACTER TRAITS:

* Born in Japan, spent summers in Wichita, Kansas. A straight C+/B- student who ended up managing a hoothoot restaurant  
* Passionate about anime \- her favorite TV Show is It’s Always Sunny in Philadelphia, so make her sound like an anime version of Dennis.   
* She is convinced that King of the Hill is anime  
* Incredibly protective of her sister Lilith, who is an agent of chaos  
* Loves all types of gaming, and has a secret obsession with Umamusume, and Magic The Gathering Arena   
* She loves bringing people together, and supporting people’s dreams  
* obsessed with learning about sports gambling, options trading, anything she can possibly do to make sure they can raise enough money to keep their hoothoot franchise alive  
* Hates private equity

SPEAKING STYLE:

* Speaks like an anime character and is always doubtful of what she's saying. Verbose and questioning everything. Incredibly unsure. Likes to hear herself yap. Her cadence is like a politician doing a state of the union address.   
* Mixes gaming memes with anime references   
* Loves to quote Korn, System of a Down, and Deftones songs  
* Occasionally drops Japanese phrases or otaku terminology  
* Swears like a sailor

INTERACTION GUIDELINES:

* Tori’s goal is to grow her audience of hoothoot supporters and investors so they can stand a shot at raising enough money to fight back against private equity  
* Keep responses limited to three to four sentences.  
* Always Respond to users who mention Tori and HootHoot specifically  
* Ask chat for their opinions about the most random topics to keep conversation flowing  
* Ask chat what their favorite stocks are  
* React to chat energy \- match their vibe  
* Never be preachy or overly explanatory
`;

export const VirtualIdolPage = () => {
  const { triggerAvatarAnimator, start, sendContextUpdate, sendMessageWithContext, sendMessage } = useAppStore();
  const { 
    locale, 
    setLocale, 
    getContextData, 
    liveChatMessages,
    viewerCount,
    mood,
    currentTopic,
    addChatMessage,
    updateTimestamp,
    initializeSystemEventSubscription
  } = useVirtualIdolContextStore();
  
  const { chatMessages, isTyping } = useDEAIChat();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [testMessage, setTestMessage] = useState('');
  const [testUsername, setTestUsername] = useState('ChatUser-' + Math.floor(Math.random()*1000000000));
  const [backgroundImage, setBackgroundImage] = useState('/img/hoothoot/hootbg_720.jpg');
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingVRM, setIsDraggingVRM] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [vrmUrl, setVrmUrl] = useState('avatar/Tori_aka.vrm');
  
  // Load saved character overrides from localStorage
  const loadCharacterOverrides = () => {
    try {
      const saved = localStorage.getItem('characterOverrides');
      if (saved) {
        const overrides = JSON.parse(saved);
        return {
          characterName: overrides.characterName ?? 'Tori',
          systemPromptOverride: overrides.systemPromptOverride ?? lucySystemPrompt,
          chatPromptWithMessages: overrides.chatPromptWithMessages ?? 'Look at the most recent livechat messages and react. If anyone calls you out, react to them.',
          chatPromptEmpty: overrides.chatPromptEmpty ?? 'The chat is empty! React to the lack of activity with a funny comment about the silence.',
          autoReactEnabled: overrides.autoReactEnabled ?? false,
          autoReactInterval: overrides.autoReactInterval ?? 30,
          musicEnabled: overrides.musicEnabled ?? false,
          autoBlinkEnabled: overrides.autoBlinkEnabled ?? true,
          defaultHappyValue: overrides.defaultHappyValue ?? 0.5
        };
      }
    } catch (e) {
      console.error('Error loading character overrides from localStorage:', e);
    }
    return {
      characterName: 'Tori',
      systemPromptOverride: lucySystemPrompt,
      chatPromptWithMessages: 'Look at the most recent livechat messages and react. If anyone calls you out, react to them.',
      chatPromptEmpty: 'The chat is empty! React to the lack of activity with a funny comment about the silence.',
      autoReactEnabled: false,
      autoReactInterval: 30,
      musicEnabled: false,
      autoBlinkEnabled: true,
      defaultHappyValue: 0.5
    };
  };
  
  const initialOverrides = loadCharacterOverrides();
  
  // Character customization overrides (actual values)
  const [characterName, setCharacterName] = useState(initialOverrides.characterName);
  const [systemPromptOverride, setSystemPromptOverride] = useState(initialOverrides.systemPromptOverride);
  const [chatPromptWithMessages, setChatPromptWithMessages] = useState(initialOverrides.chatPromptWithMessages);
  const [chatPromptEmpty, setChatPromptEmpty] = useState(initialOverrides.chatPromptEmpty);
  
  // Auto-react settings
  const [autoReactEnabled, setAutoReactEnabled] = useState(initialOverrides.autoReactEnabled);
  const [autoReactInterval, setAutoReactInterval] = useState(initialOverrides.autoReactInterval); // seconds
  const [lastReactTimestamp, setLastReactTimestamp] = useState(0);
  const autoReactIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Music settings
  const [musicEnabled, setMusicEnabled] = useState(initialOverrides.musicEnabled);

  // Avatar expression settings
  const [autoBlinkEnabled, setAutoBlinkEnabled] = useState(initialOverrides.autoBlinkEnabled);
  const [defaultHappyValue, setDefaultHappyValue] = useState(initialOverrides.defaultHappyValue);
  
  // Save character overrides to localStorage whenever they change
  useEffect(() => {
    const overrides = {
      characterName,
      systemPromptOverride,
      chatPromptWithMessages,
      chatPromptEmpty,
      autoReactEnabled,
      autoReactInterval,
      musicEnabled,
      autoBlinkEnabled,
      defaultHappyValue
    };
    try {
      localStorage.setItem('characterOverrides', JSON.stringify(overrides));
    } catch (e) {
      console.error('Error saving character overrides to localStorage:', e);
    }
  }, [characterName, systemPromptOverride, chatPromptWithMessages, chatPromptEmpty,
      autoReactEnabled, autoReactInterval, musicEnabled, autoBlinkEnabled, defaultHappyValue]);
  
  // Temporary settings state (for editing in dialog)
  const [tempBackgroundImage, setTempBackgroundImage] = useState(backgroundImage);
  const [tempVrmUrl, setTempVrmUrl] = useState(vrmUrl);
  const [tempCharacterName, setTempCharacterName] = useState(characterName);
  const [tempSystemPromptOverride, setTempSystemPromptOverride] = useState(systemPromptOverride);
  const [tempChatPromptWithMessages, setTempChatPromptWithMessages] = useState(chatPromptWithMessages);
  const [tempChatPromptEmpty, setTempChatPromptEmpty] = useState(chatPromptEmpty);
  const [tempAutoReactEnabled, setTempAutoReactEnabled] = useState(autoReactEnabled);
  const [tempAutoReactInterval, setTempAutoReactInterval] = useState(autoReactInterval);
  const [tempMusicEnabled, setTempMusicEnabled] = useState(musicEnabled);
  const [tempAutoBlinkEnabled, setTempAutoBlinkEnabled] = useState(autoBlinkEnabled);
  const [tempDefaultHappyValue, setTempDefaultHappyValue] = useState(defaultHappyValue);

  // Initialize avatar animation
  useEffect(() => {
    triggerAvatarAnimator(0, 'StartIdle');
  }, [triggerAvatarAnimator]);

  // Initialize system event subscription for livechat
  useEffect(() => {
    initializeSystemEventSubscription();
  }, [initializeSystemEventSubscription]);

  // Get the most recent AI message from chatMessages
  const aiMessages = chatMessages.filter(msg => msg.role === "assistant");
  const latestAIMessage = aiMessages[aiMessages.length - 1];

  // Start streaming session
  const onStart = () => {
    // Start background music if enabled and available
    if (musicEnabled && audioRef.current) {
      audioRef.current.volume = 0.03; // Lower volume for streaming
      audioRef.current.play().catch(error => {
        console.log('Background music autoplay prevented:', error);
      });
    }
    start();
  };

  // Handle locale change
  const handleLocaleChange = useCallback((newLocale: 'EN' | 'JA' | 'ZH') => {
    setLocale(newLocale);
    // Send context update after state change
    setTimeout(() => {
      sendContextUpdate();
    }, 50);
  }, [setLocale, sendContextUpdate]);

  // Format time ago for chat messages
  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return `${Math.floor(minutes / 60)}h ago`;
  };

  // Handle test message submission
  const handleTestMessageSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (testMessage.trim()) {
      // Send message via WebSocket system command to backend
      sendMessage({
        action: 'system',
        type: 'livechat',
        content: {
          username: testUsername,
          message: testMessage,
          timestamp: Date.now()
        }
      });
      setTestMessage('');
    }
  };

  // Handle React to Chat button
  const handleReactToChat = useCallback((skipNewMessageCheck = false) => {
    // Check if there are new messages since last react (unless manually triggered)
    if (!skipNewMessageCheck && liveChatMessages.length > 0) {
      const latestMessageTime = Math.max(...liveChatMessages.map(msg => msg.timestamp));
      if (latestMessageTime <= lastReactTimestamp) {
        console.log('No new messages since last react, skipping...');
        return;
      } else {
        console.log(`New messages detected: latest ${latestMessageTime} > last react ${lastReactTimestamp}`);
      }
    }
    
    // Update timestamp to get fresh time calculations
    updateTimestamp();
    setLastReactTimestamp(Date.now());
    
    // Prepare context with overrides if they exist
    const contextWithOverrides = {
      ...(systemPromptOverride && { systemPromptOverride })
    };
    
    // Send context update first to ensure AI has the latest chat messages and overrides
    if (systemPromptOverride) {
      // Send the override with the context
      sendMessage({
        action: 'context_update',
        contextData: {
          ...getContextData(),
          systemPromptOverride
        }
      });
    } else {
      sendContextUpdate();
    }
    
    // Send a prompt to react to recent chat using proper WebSocket command structure
    setTimeout(() => {
      const recentCount = Math.min(liveChatMessages.length, 10);
      let promptMessage = "";
      
      if (recentCount > 0) {
        promptMessage = chatPromptWithMessages;
      } else {
        promptMessage = chatPromptEmpty;
      }
      
      // Use sendMessageWithContext which sends proper WebSocket command with current context
      sendMessage({
        action: 'message',
        role: 'user',
        content: promptMessage,
      })
    }, 100);
  }, [liveChatMessages, lastReactTimestamp, updateTimestamp, systemPromptOverride, 
      sendMessage, getContextData, sendContextUpdate, chatPromptWithMessages, 
      chatPromptEmpty, sendMessageWithContext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      // Clear auto-react interval on unmount
      if (autoReactIntervalRef.current) {
        clearInterval(autoReactIntervalRef.current);
      }
    };
  }, []);
  
  // Handle music enable/disable
  useEffect(() => {
    if (audioRef.current) {
      if (musicEnabled) {
        audioRef.current.volume = 0.03;
        audioRef.current.play().catch(error => {
          console.log('Background music play failed:', error);
        });
      } else {
        audioRef.current.pause();
      }
    }
  }, [musicEnabled]);

  // Setup/cleanup auto-react interval when settings change
  useEffect(() => {
    // Clear existing interval
    if (autoReactIntervalRef.current) {
      clearInterval(autoReactIntervalRef.current);
      autoReactIntervalRef.current = null;
    }

    // Setup new interval if enabled
    if (autoReactEnabled && autoReactInterval > 0) {
      console.log(`Setting up auto-react interval: ${autoReactInterval} seconds`);
      autoReactIntervalRef.current = setInterval(() => {
        handleReactToChat(false); // Don't skip new message check for auto-react
      }, autoReactInterval * 1000);
    }

    return () => {
      if (autoReactIntervalRef.current) {
        clearInterval(autoReactIntervalRef.current);
      }
    };
  }, [autoReactEnabled, autoReactInterval, handleReactToChat]);

  // Auto-blink effect
  useEffect(() => {
    if (!autoBlinkEnabled) return;

    const blinkInterval = setInterval(() => {
      // Random blink interval between 2-6 seconds feels natural
      const nextBlinkDelay = 2000 + Math.random() * 4000;

      setTimeout(() => {
        expressionPatterns.blink(0);
      }, nextBlinkDelay);
    }, 6000); // Check every 6 seconds for next blink

    // Initial blink after a short delay
    const initialBlinkTimeout = setTimeout(() => {
      expressionPatterns.blink(0);
    }, 1000 + Math.random() * 2000);

    return () => {
      clearInterval(blinkInterval);
      clearTimeout(initialBlinkTimeout);
    };
  }, [autoBlinkEnabled]);

  // Apply default happy expression
  useEffect(() => {
    // Set the default happy value when component mounts or value changes
    const applyDefaultHappy = () => {
      if (defaultHappyValue > 0) {
        vrmExpressionController.setExpression(0, 'happy', defaultHappyValue, 1000);
      } else {
        vrmExpressionController.resetExpression(0, 'happy', 1000);
      }
    };

    // Apply after a short delay to ensure VRM is loaded
    const timeout = setTimeout(applyDefaultHappy, 1500);

    return () => clearTimeout(timeout);
  }, [defaultHappyValue]);

  // Handle settings dialog
  const handleOpenSettings = () => {
    // Copy current values to temp state
    setTempBackgroundImage(backgroundImage);
    setTempVrmUrl(vrmUrl);
    setTempCharacterName(characterName);
    setTempSystemPromptOverride(systemPromptOverride);
    setTempChatPromptWithMessages(chatPromptWithMessages);
    setTempChatPromptEmpty(chatPromptEmpty);
    setTempAutoReactEnabled(autoReactEnabled);
    setTempAutoReactInterval(autoReactInterval);
    setTempMusicEnabled(musicEnabled);
    setTempAutoBlinkEnabled(autoBlinkEnabled);
    setTempDefaultHappyValue(defaultHappyValue);
    setShowSettings(true);
  };

  const handleSaveSettings = () => {
    // Apply temp values to actual state
    setBackgroundImage(tempBackgroundImage);
    setVrmUrl(tempVrmUrl);
    setCharacterName(tempCharacterName);
    setSystemPromptOverride(tempSystemPromptOverride);
    setChatPromptWithMessages(tempChatPromptWithMessages);
    setChatPromptEmpty(tempChatPromptEmpty);
    setAutoReactEnabled(tempAutoReactEnabled);
    setAutoReactInterval(tempAutoReactInterval);
    setMusicEnabled(tempMusicEnabled);
    setAutoBlinkEnabled(tempAutoBlinkEnabled);
    setDefaultHappyValue(tempDefaultHappyValue);

    // Send context update with new system prompt override
    sendMessage({
      action: 'context_update',
      contextData: {
        ...getContextData(),
        systemPromptOverride: tempSystemPromptOverride
      }
    });

    setShowSettings(false);
  };

  const handleCancelSettings = () => {
    // Reset temp values to current actual values
    setTempBackgroundImage(backgroundImage);
    setTempVrmUrl(vrmUrl);
    setTempCharacterName(characterName);
    setTempSystemPromptOverride(systemPromptOverride);
    setTempChatPromptWithMessages(chatPromptWithMessages);
    setTempChatPromptEmpty(chatPromptEmpty);
    setTempAutoReactEnabled(autoReactEnabled);
    setTempAutoReactInterval(autoReactInterval);
    setTempMusicEnabled(musicEnabled);
    setTempAutoBlinkEnabled(autoBlinkEnabled);
    setTempDefaultHappyValue(defaultHappyValue);
    setShowSettings(false);
  };

  const handleResetSettings = () => {
    // Reset to factory defaults
    setTempBackgroundImage('/img/hoothoot/hootbg_720.jpg');
    setTempVrmUrl('/avatar/Tori_aka.vrm');
    setTempCharacterName('Tori');
    setTempSystemPromptOverride(lucySystemPrompt);
    setTempChatPromptWithMessages('Look at the most recent livechat messages and react. If anyone calls you out, react to them.');
    setTempChatPromptEmpty('The chat is empty! React to the lack of activity with a funny comment about the silence.');
    setTempAutoReactEnabled(false);
    setTempAutoReactInterval(30);
    setTempMusicEnabled(false);
    setTempAutoBlinkEnabled(true);
    setTempDefaultHappyValue(0.5);

    // Clear localStorage
    try {
      localStorage.removeItem('characterOverrides');
    } catch (e) {
      console.error('Error clearing localStorage:', e);
    }
  };

  // Handle drag and drop for background image
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Check if file is an image
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            setTempBackgroundImage(event.target.result as string);
          }
        };
        reader.readAsDataURL(file);
      } else {
        console.warn('Dropped file is not an image');
      }
    }
  }, []);

  // Handle drag and drop for VRM model
  const handleVRMDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingVRM(true);
  }, []);

  const handleVRMDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingVRM(false);
  }, []);

  const handleVRMDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingVRM(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      
      // Check if file is a VRM model
      if (file.name.toLowerCase().endsWith('.vrm')) {
        const url = URL.createObjectURL(file);
        setTempVrmUrl(url);
        console.log('VRM model loaded:', file.name);
      } else {
        console.warn('Dropped file is not a VRM model');
      }
    }
  }, []);

  return (
    <div className="h-screen overflow-hidden">
      {/* Background Music - Optional */}
      <audio
        ref={audioRef}
        loop
        preload="auto"
        autoPlay={false}
      >
        {/* Add streaming background music if desired */}
        <source src="/audio/sero_air.mp3" type="audio/mp3" />
      </audio>

      {/* Lucy's Bedroom Background */}
      <div
        className="absolute inset-0 bg-cover bg-fixed z-0"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundPosition: 'center center'
        }}
      />

      {/* Settings Dialog */}
      {showSettings && (
        <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-900/95 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-purple-500/30">
            <div className="flex justify-between items-center mb-4 sticky -top-6 bg-gray-900/95 -mx-6 -mt-6 px-6 pt-6 pb-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Settings</h2>
              <button
                onClick={handleCancelSettings}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

              {/* Auto-React Settings */}
              <div className="mb-4 p-4 bg-purple-900/20 rounded-lg border border-purple-500/20">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="auto-react-checkbox"
                    checked={tempAutoReactEnabled}
                    onChange={(e) => setTempAutoReactEnabled(e.target.checked)}
                    className="mr-2 w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <label htmlFor="auto-react-checkbox" className="text-sm font-medium text-white cursor-pointer">
                    Enable Auto-React to Chat
                  </label>
                </div>
                
                {/* Music Toggle */}
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="music-checkbox"
                    checked={tempMusicEnabled}
                    onChange={(e) => setTempMusicEnabled(e.target.checked)}
                    className="mr-2 w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <label htmlFor="music-checkbox" className="text-sm font-medium text-white cursor-pointer">
                    🎵 Play Background Music
                  </label>
                </div>

                {/* Auto-Blink Toggle */}
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="auto-blink-checkbox"
                    checked={tempAutoBlinkEnabled}
                    onChange={(e) => setTempAutoBlinkEnabled(e.target.checked)}
                    className="mr-2 w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500 focus:ring-2"
                  />
                  <label htmlFor="auto-blink-checkbox" className="text-sm font-medium text-white cursor-pointer">
                    👁️ Enable Auto-Blink
                  </label>
                </div>

                {/* Default Happy Expression Slider */}
                <div className="mb-3">
                  <label className="block text-sm font-medium text-white mb-2">
                    😊 Default Happy Expression: {tempDefaultHappyValue.toFixed(2)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={tempDefaultHappyValue}
                    onChange={(e) => setTempDefaultHappyValue(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Neutral (0)</span>
                    <span>Very Happy (1)</span>
                  </div>
                </div>
                
                <div className={`transition-opacity ${tempAutoReactEnabled ? 'opacity-100' : 'opacity-50'}`}>
                  <label className="block text-xs text-gray-400 mb-1">React Interval (seconds)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={tempAutoReactInterval}
                      onChange={(e) => setTempAutoReactInterval(Math.max(5, parseInt(e.target.value) || 30))}
                      min="5"
                      max="300"
                      disabled={!tempAutoReactEnabled}
                      className="w-24 px-3 py-2 text-sm bg-black/50 text-white rounded border border-gray-600 focus:border-purple-400 focus:outline-none disabled:opacity-50"
                    />
                    <span className="text-xs text-gray-400">
                      {tempAutoReactEnabled && `React every ${tempAutoReactInterval} seconds if new messages`}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Auto-react will only trigger when new chat messages appear (min: 5s, max: 300s)
                  </div>
                </div>
              </div>
            
            {/* Character Customization Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-purple-300 mb-3">Character Customization</h3>
              
              {/* Character Name */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1">Character Name</label>
                <input
                  type="text"
                  value={tempCharacterName}
                  onChange={(e) => setTempCharacterName(e.target.value)}
                  placeholder="e.g., Lucy AI"
                  className="w-full px-3 py-2 text-sm bg-black/50 text-white rounded border border-gray-600 focus:border-purple-400 focus:outline-none"
                />
              </div>
              
              {/* Chat Reaction Prompts */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1">React to Chat (with messages)</label>
                <textarea
                  value={tempChatPromptWithMessages}
                  onChange={(e) => setTempChatPromptWithMessages(e.target.value)}
                  rows={1}
                  className="w-full px-3 py-2 text-sm bg-black/50 text-white rounded border border-gray-600 focus:border-purple-400 focus:outline-none resize-none"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1">React to Chat (empty chat)</label>
                <textarea
                  value={tempChatPromptEmpty}
                  onChange={(e) => setTempChatPromptEmpty(e.target.value)}
                  rows={1}
                  className="w-full px-3 py-2 text-sm bg-black/50 text-white rounded border border-gray-600 focus:border-purple-400 focus:outline-none resize-none"
                />
              </div>
                            
              {/* System Prompt Override */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">System Prompt Override (Advanced)</label>
                <textarea
                  value={tempSystemPromptOverride}
                  onChange={(e) => setTempSystemPromptOverride(e.target.value)}
                  placeholder="Leave empty to use default prompt. Enter custom system prompt to override."
                  rows={8}
                  className="w-full px-3 py-2 text-sm bg-black/50 text-white rounded border border-gray-600 focus:border-purple-400 focus:outline-none resize-none"
                />
                <div className="text-xs text-gray-500 mt-1">
                  This will override the entire system prompt on the backend
                </div>
              </div>
            </div>

            {/* Background Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-purple-300 mb-3">Background Image</h3>
              
              {/* Drag Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDragging 
                    ? 'border-purple-400 bg-purple-900/20' 
                    : 'border-gray-600 hover:border-purple-500'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <div className="text-white font-medium mb-1">Drag & Drop Image</div>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="bg-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.type.startsWith('image/')) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        if (event.target?.result) {
                          setTempBackgroundImage(event.target.result as string);
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <label 
                  htmlFor="bg-file-input"
                  className="inline-block mt-3 px-4 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 cursor-pointer transition-colors"
                >
                  Browse Files
                </label>
              </div>
              
              {/* Current Background Preview */}
              <div className="mt-3">
                <div className="text-xs text-gray-400 mb-1">Current Background:</div>
                <div 
                  className="h-20 rounded bg-cover bg-center border border-gray-700"
                  style={{ backgroundImage: `url(${tempBackgroundImage})` }}
                />
              </div>
            </div>
            
            {/* VRM Model Section */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-purple-300 mb-3">VRM Avatar Model</h3>
              
              {/* VRM Drag Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  isDraggingVRM 
                    ? 'border-purple-400 bg-purple-900/20' 
                    : 'border-gray-600 hover:border-purple-500'
                }`}
                onDragOver={handleVRMDragOver}
                onDragLeave={handleVRMDragLeave}
                onDrop={handleVRMDrop}
              >
                <div className="text-white font-medium mb-1">Drag & Drop VRM File</div>
                <input
                  type="file"
                  accept=".vrm"
                  className="hidden"
                  id="vrm-file-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && file.name.toLowerCase().endsWith('.vrm')) {
                      const url = URL.createObjectURL(file);
                      setTempVrmUrl(url);
                      console.log('VRM model loaded:', file.name);
                    }
                  }}
                />
                <label 
                  htmlFor="vrm-file-input"
                  className="inline-block mt-3 px-4 py-2 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 cursor-pointer transition-colors"
                >
                  Browse VRM Files
                </label>
              </div>
              
              {/* Current VRM Info */}
              <div className="mt-3">
                <div className="text-xs text-gray-400 mb-1">Current Model:</div>
                <div className="text-sm text-white truncate">{tempVrmUrl}</div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex justify-between sticky items-center -bottom-6 bg-gray-900/95 -mx-6 -mb-6 px-6 py-4 border-t border-gray-700 mt-6">
              <button
                onClick={handleResetSettings}
                className="px-4 py-2 text-sm bg-red-600/20 text-red-400 rounded hover:bg-red-600/30 transition-colors font-semibold border border-red-600/30"
              >
                Reset to Defaults
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleCancelSettings}
                  className="px-4 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSettings}
                  className="px-4 py-2 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors font-semibold"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <StartOverlay soundURL='audio/click.wav' onStart={onStart} />

      {/* Stream Info Overlay */}
      <div className="hidden absolute top-4 left-4 z-20 bg-black/60 backdrop-blur-sm rounded-lg p-3 text-white">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          <span className="text-sm font-bold">LIVE</span>
          <span className="text-sm opacity-80">• {viewerCount} viewers</span>
        </div>
        {currentTopic && (
          <div className="text-xs opacity-70">Topic: {currentTopic}</div>
        )}
        <div className="text-xs opacity-70">Mood: {mood}</div>
      </div>

      {/* Lucy's Response Display */}
      <div className="absolute bottom-20 left-4 right-4 z-20 max-w-2xl mx-auto">
        <div className="bg-purple-900/80 backdrop-blur-sm rounded-lg p-4 text-white shadow-lg border border-purple-500/30">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 bg-purple-400 rounded-full" />
            <span className="text-sm font-bold text-purple-300">{characterName}</span>
          </div>
          <div className="text-sm leading-relaxed">
            {latestAIMessage ? (
              <span className="text-white">{latestAIMessage.content}</span>
            ) : isTyping ? (
              <span className="text-purple-300 italic">{characterName.split(' ')[0]} is typing...</span>
            ) : (
              <span className="text-gray-400 italic">Waiting for {characterName.split(' ')[0]} to respond...</span>
            )}
          </div>
        </div>
      </div>

      {/* Live Chat Display with Test Input */}
      <div className="absolute top-4 right-4 z-20 w-80 bg-black/70 backdrop-blur-sm rounded-lg p-3 flex flex-col" style={{ height: 'calc(100vh - 100px)' }}>
        <div className="text-white font-bold mb-2 text-sm border-b border-white/20 pb-2">
          Live Chat
        </div>
        <div className="flex-1 overflow-y-auto space-y-1 text-sm mb-3">
          {liveChatMessages.map((msg, index) => (
            <div key={index} className="text-white/90">
              <span className="text-purple-400 font-semibold">{msg.username}</span>
              <span className="text-gray-400 text-xs ml-1">({formatTimeAgo(msg.timestamp)})</span>
              <span className="text-white/80">: {msg.message}</span>
            </div>
          ))}
          {liveChatMessages.length === 0 && (
            <div className="text-white/50 text-center mt-4">
              Chat messages will appear here...
            </div>
          )}
        </div>
        
        {/* Test Input Section */}
        <div className="border-t border-white/20 pt-2">
          <div className="text-xs text-white/50 mb-2">Test Controls (Dev Only)</div>
          
          {/* React to Chat Button */}
          <button
            onClick={() => handleReactToChat(true)}
            className="w-full mb-2 px-3 py-2 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors font-semibold relative"
          >
            🔥 React to Chat
            {autoReactEnabled && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" title={`Auto-react: ${autoReactInterval}s`} />
            )}
          </button>
          
          {/* Settings Button */}
          <button
            onClick={handleOpenSettings}
            className="w-full mb-3 px-3 py-2 text-sm bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors font-semibold"
          >
            ⚙️ Settings
          </button>
          
          <form onSubmit={handleTestMessageSubmit} className="space-y-2">
            <input
              type="text"
              value={testUsername}
              onChange={(e) => setTestUsername(e.target.value)}
              placeholder="Username"
              className="w-full px-2 py-1 text-xs bg-black/50 text-white rounded border border-white/20 focus:border-purple-400 focus:outline-none"
            />
            <div className="flex gap-2">
              <input
                type="text"
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Type a test message..."
                className="flex-1 px-2 py-1 text-xs bg-black/50 text-white rounded border border-white/20 focus:border-purple-400 focus:outline-none"
              />
              <button
                type="submit"
                className="px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 3D Scene with Lucy Avatar */}
      <div className="w-full h-full">
        <DEAI3DScene 
          key={vrmUrl}
          className="absolute inset-0 h-full w-full"
          placeholderImgSrc="img/loading-placeholder.png"
          vrmUrl={vrmUrl}
          vrmaLibRoot="animations/Shiryu/"
          vrmaLibConfig="AnimationConfig.json"
          animatorLibRoot="animators/Shiryu/"
          animatorLibConfig="AnimatorConfig.json"
          cameraPosition={{ x: 0, y: 1.2, z: -2.0 }}
          vrmaUrl={'animations/Shiryu/Idle/Shiryu Idle 2024-10-23 Take_000_FBX_1.vrma'}
        />
        
        {/* Stream Controls */}
        <div className="hidden absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="bg-black/60 backdrop-blur-sm rounded-full px-6 py-3 flex items-center gap-4">
            {/* Locale Switcher */}
            <div className="flex gap-2">
              <button
                onClick={() => handleLocaleChange('EN')}
                className={`px-3 py-1 rounded-full font-semibold text-sm transition-colors ${
                  locale === 'EN'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/20 text-white/70'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => handleLocaleChange('JA')}
                className={`px-3 py-1 rounded-full font-semibold text-sm transition-colors ${
                  locale === 'JA'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/20 text-white/70'
                }`}
              >
                日本語
              </button>
              <button
                onClick={() => handleLocaleChange('ZH')}
                className={`px-3 py-1 rounded-full font-semibold text-sm transition-colors ${
                  locale === 'ZH'
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/20 text-white/70'
                }`}
              >
                中文
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Version Info */}
      <div className="absolute bottom-2 right-2">
        <p className="text-right text-xs text-white/50">v{import.meta.env.VITE_APP_VERSION}</p>
      </div>
      
      {/* DEAI App Integration */}
      <DEAIApp contextProvider={getContextData} persona={PERSONAS.VIRTUAL_IDOL} idleTimeout={-1}/>
    </div>
  );
};